/**
 * Turn a failed document-review submit into an outcome plus what to do about it.
 *
 * Pure on purpose: this is the piece that changes whenever the backend adds an
 * error code, so it is worth testing without a model or a fetch mock. The
 * caller owns the side effects; this only decides.
 *
 * @param {Error & {status?: number, responseData?: object}} error
 * @returns {{outcome: string, message?: string, invalidFields?: string[],
 *            remove?: boolean, refresh?: boolean}}
 *   `remove` — drop the document; `refresh` — re-fetch its flags. Either one
 *   changes the queue, so the caller recomputes phase from what is left.
 */
export function interpretCommitError(error) {
  // Error bodies are flat: { error: "...", ... }. Some deployments nest the
  // same object under `detail`, so check both.
  const body = error.responseData?.detail || error.responseData || {};

  // 409 not_awaiting_review — already resolved, or a stale tab / double submit.
  // Not an error, and explicitly do NOT retry: drop it and carry on with the
  // rest of the dataset. 404 (gone, or not ours) is the same move.
  if (error.status === 409 || error.status === 404) {
    return { outcome: 'conflict', remove: true };
  }

  if (error.status === 422) {
    // `missing` is a list of entity_ids.
    if (body.error === 'incomplete_review') {
      return {
        outcome: 'incomplete',
        invalidFields: (body.missing || []).map(m => (typeof m === 'string' ? m : m.entity_id)),
        message: 'Some fields still need a decision. Complete the highlighted fields.',
      };
    }
    // entity_not_flagged means our snapshot is stale — the id is neither
    // currently flagged nor previously settled. Replace the flag set wholesale
    // rather than trying to diff it.
    if (body.error === 'entity_not_flagged') {
      return {
        outcome: 'stale',
        message: 'This document changed while you were reviewing it. Reloaded its fields.',
        refresh: true,
      };
    }
    // Other per-resolution rejections name the offending entity_id.
    const perResolution = {
      corrected_value_required: 'A corrected value is required for the highlighted field.',
      unknown_action: 'That action is not recognised for the highlighted field.',
    };
    if (perResolution[body.error]) {
      return {
        outcome: 'invalid',
        message: perResolution[body.error],
        invalidFields: body.entity_id ? [body.entity_id] : undefined,
      };
    }
  }

  return { outcome: 'error', message: error.message || 'Failed to save this document' };
}

export default interpretCommitError;
